import { createContext, useContext } from "react";

import type { TContextFeature, TOperationalFeature } from "safesystem_geoserver-api";

import {
	EIdentify,
	EModule,
	EPrioritize,
	type ISystemLayers,
	type TLayerId,
	EAccidents,
} from "@/models/saferoads/interfaces";
import { FetchStatus } from "@/lib/defines";
import type { MapLayer, MapLegendItem, QueryableLayer } from "@/store/legendSlice";
import { priority_map } from "@/models/saferoads/columns";

type GeodataContext = {
	getGeodata: (apikey: string) => Promise<void>;
	featureCounts: Record<TOperationalFeature["id"], number>;
	fetchStatus: FetchStatus;
	contextLayer: TContextFeature;
	systemData: ISystemLayers;
	systemDataReady: boolean;
};

export const GeodataContext = createContext<GeodataContext | undefined>(undefined);

export const useGeodata = () => {
	const context = useContext(GeodataContext);
	if (context === undefined) {
		throw new Error("useGeodata must be used within a GeodataProvider");
	}
	return context;
};

const queryableMapLayers: TLayerId[] = [
	EIdentify.Identify,
	EPrioritize.Prioritize,
];

export function parseLegendLayers(payload: ISystemLayers) {
	const mapLayers: MapLayer[] = [];
	Object.values(EModule).forEach((module) => {
		const features = payload[module];
		Object.values(features).forEach((feature) => {
			const legendItems: MapLegendItem[] = [];
			let filterAttr: string = "";
			let defaultEnabled = false;
			let queryProps: QueryableLayer = { includeInQuery: false, queryProperties: [] };

			if (feature.id === EIdentify.Identify) {
				defaultEnabled = true;
				// @ts-expect-error Unpacking these variable types is hard
				const paintArray = feature.layer.paint["line-color"] as Array<string>;
				filterAttr = paintArray[1][1];
				for (let i = 2; i < paintArray.length - 1; i += 2) {
					const label = paintArray[i];
					legendItems.push({
						legendItemId: `${feature.id}:${label.replace(" ", "_")}`,
						filterValue: paintArray[i],
						label: label,
						color: paintArray[i + 1],
						enabled: true,
					});
				}
			} else if (feature.id === EPrioritize.Prioritize) {
				defaultEnabled = false;
				// @ts-expect-error Unpacking these variable types is hard
				const paintArray = feature.layer.paint["line-color"] as Array<string>;
				filterAttr = paintArray[1][1];
				for (let i = 2; i < paintArray.length - 1; i += 2) {
					const label = priority_map[paintArray[i]];
					legendItems.push({
						legendItemId: `${feature.id}:${label.replace(" ", "_")}`,
						filterValue: paintArray[i],
						label,
						color: paintArray[i + 1],
						enabled: false,
					});
				}
			} else if (feature.id === EAccidents.AccidentsCircle) {
				// @ts-expect-error Unpacking these variable types is hard
				const paintColor = feature.layer.paint["circle-color"] as string;
				legendItems.push({
					legendItemId: `${feature.id}:circle`,
					label: "",
					color: paintColor,
					enabled: defaultEnabled,
				});
			} else if (feature.id == EAccidents.AccidentsHeatmap) {
				legendItems.push({
					legendItemId: `${feature.id}:heatmap`,
					label: "",
					color: "#ddd",
					enabled: defaultEnabled,
				});
			} else if (feature.id == EAccidents.UploadedCrashes) {
				defaultEnabled = true;
				// @ts-expect-error Unpacking these variable types is hard
				const paintArray = feature.layer.paint["circle-color"] as Array<string>;
				filterAttr = paintArray[1][1];
				for (let i = 2; i < paintArray.length - 1; i += 2) {
					const label = paintArray[i];
					legendItems.push({
						legendItemId: `${feature.id}:${label.replace(" ", "_")}`,
						filterValue: paintArray[i],
						label: label,
						color: paintArray[i + 1],
						enabled: defaultEnabled,
					});
				}
			}
			/** setup query props for the layer */
			if (queryableMapLayers.includes(feature.id)) {
				queryProps = { includeInQuery: true, queryProperties: feature.queryProperties };
			}
			mapLayers.push({
				systemId: feature.id,
				layerId: feature.layer.id as string,
				initialFilter: feature.layer.filter,
				minzoom: feature.layer.minzoom,
				filterAttr,
				legendItems,
				layerAllEnabled: defaultEnabled,
				layerAnyEnabled: defaultEnabled,
				...queryProps,
			});
		});
	});
	return mapLayers;
}
